import { useState, type ReactNode } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { thankYouContent } from '../content/thankYou';
import { pageSeo, type PageSeo } from '../content/seo';
import { Seo } from '../components/seo/Seo';
import { Section } from '../components/layout/Section';
import { ReferralForm } from '../components/referral/ReferralForm';
import { readSignupRef, type SignupAudience } from '../lib/signupSession';

/**
 * Post-signup confirmation pages (spec sections 05 and 10).
 *
 * **Gating.** "Thank you pages load only after a successful conversion flow"
 * (spec section 22). The gate is the signup reference the API returned, held in
 * `sessionStorage`: no reference, and the visitor is sent to the form they were
 * looking for rather than shown a confirmation for something that never
 * happened. Both routes are noindex on top of that.
 *
 * The check is presence only. The token is signed and the server is what
 * verifies it, when Stage B's referral endpoint uses it to attach a
 * recommendation to the lead. A client-side verification here would be
 * theatre — anything the browser can check, the browser can also fake.
 *
 * The reference is deliberately *not* in the URL: no personal data and no
 * signup reference in a link that could be copied, logged, or leaked in a
 * referrer header (spec sections 05 and 19).
 */

interface ThankYouPageProps {
  audience: SignupAudience;
  seo: PageSeo;
  formPath: string;
  eyebrow: string;
  headline: string;
  body: readonly string[];
  /** Audience-specific block between the confirmation and the referral ask. */
  children?: ReactNode;
}

function ThankYouPage({
  audience,
  seo,
  formPath,
  eyebrow,
  headline,
  body,
  children,
}: ThankYouPageProps) {
  // Read once, on mount. A later render must not change the answer under a
  // visitor who is reading the page.
  const [converted] = useState(() => readSignupRef(audience) !== null);

  if (!converted) return <Navigate to={formPath} replace />;

  return (
    <>
      <Seo seo={seo} />

      <Section labelledBy="thank-you-heading">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-signal">{eyebrow}</p>
        <h1
          id="thank-you-heading"
          className="mt-3 max-w-3xl text-3xl font-semibold leading-tight tracking-tight text-ink sm:text-4xl"
        >
          {headline}
        </h1>
        {body.map((paragraph) => (
          <p key={paragraph} className="mt-5 max-w-2xl text-base leading-relaxed text-ink/85">
            {paragraph}
          </p>
        ))}
        {children}
      </Section>

      {/*
        The referral ask is a separate section from the confirmation above it,
        which is what keeps a failed recommendation from touching a signup that
        already succeeded (spec section 10: "Do not block confirmation on this
        step").
      */}
      <Section tone="warm" labelledBy="thank-you-referral">
        <ReferralForm audience={audience} headingId="thank-you-referral" />
        <p className="mt-8">
          <Link to="/" className="text-ink underline underline-offset-4">
            {thankYouContent.backLinkLabel}
          </Link>
        </p>
      </Section>
    </>
  );
}

export function ThankYouOwnerPage() {
  const content = thankYouContent.owner;
  return (
    <ThankYouPage
      audience="owner"
      seo={pageSeo.thankYouOwner}
      formPath={content.formPath}
      eyebrow={content.eyebrow}
      headline={content.headline}
      body={content.body}
    />
  );
}

export function ThankYouProviderPage() {
  const content = thankYouContent.provider;

  return (
    <ThankYouPage
      audience="provider"
      seo={pageSeo.thankYouProvider}
      formPath={content.formPath}
      eyebrow={content.eyebrow}
      headline={content.headline}
      body={content.body}
    >
      <ol className="mt-10 grid list-none gap-6 p-0 sm:grid-cols-2">
        {content.steps.map((step) => (
          <li key={step.label} className="border-t-2 border-utility pt-4">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-signal">
              {step.label}
            </p>
            <h2 className="mt-2 text-lg font-semibold text-ink">{step.title}</h2>
            <p className="mt-2 text-sm leading-relaxed text-ink/85">{step.body}</p>
          </li>
        ))}
      </ol>
      <p className="mt-8 max-w-2xl text-sm leading-relaxed text-ink/85">
        {content.termsNote.prefix}{' '}
        <Link to={content.termsNote.linkTo} className="underline underline-offset-4">
          {content.termsNote.linkLabel}
        </Link>
        {content.termsNote.suffix}
      </p>
    </ThankYouPage>
  );
}
